<i18n locale="en">
{
  "placeholders": {
    "fallback": "Select a value"
  },
  "labels": {
    "empty": "No options found"
  }
}
</i18n>

<template>
  <SelectRoot
    v-model="modelValue"
    class="own-select"
    :disabled="disabled"
    :multiple="multiple"
  >
    <SelectTrigger
      :data-testid="'own-select-trigger'"
      class="own-select__trigger rounded-xl px-2.5 py-2"
      :class="[
        error && 'own-select__trigger--error',
        disabled && 'own-select__trigger--disabled',
      ]"
      :aria-invalid="error"
      :aria-disabled="disabled"
    >
      <div class="align-center w-full flex-row justify-between gap-2">
        <div
          class="own-select__trigger-text-container align-center flex-row gap-2"
        >
          <slot name="trigger-left" :item="selectedValue">
            <Component
              :is="selectedValue?.icon"
              v-if="selectedValue?.icon"
              class="flex-shrink-0"
              size="16"
            />
          </slot>

          <SelectValue
            class="text--truncate"
            :placeholder="placeholder ?? t('placeholders.fallback')"
          >
            {{
              selectedValue?.label || placeholder || t('placeholders.fallback')
            }}
          </SelectValue>
        </div>

        <PhCaretDown
          class="text-color-primary flex-shrink-0"
          size="16"
          weight="bold"
        />
      </div>
    </SelectTrigger>

    <SelectPortal>
      <SelectContent
        class="own-select__content"
        position="popper"
        :side-offset="8"
        :style="{ '--max-content-height': maxContentHeight }"
      >
        <SelectViewport class="own-select__viewport">
          <template v-if="options.length > 0">
            <SelectItem
              v-for="option in options"
              :key="option.value"
              :value="option.value.toString()"
              class="own-select__item"
            >
              <div class="own-select__item-content">
                <div class="own-select__item-left">
                  <slot name="left" :item="option">
                    <Component
                      :is="option.icon"
                      v-if="option.icon"
                      class="flex-shrink-0"
                      size="24"
                    />
                  </slot>
                </div>

                <SelectItemText class="own-select__item-text">
                  <slot name="label" :item="option">
                    <OwnType
                      class="text--truncate flex-1"
                      el="span"
                      :text="option.label"
                      variant="title-regular"
                      color="inherit"
                      :title="option.label"
                    />
                  </slot>
                </SelectItemText>

                <div class="own-select__item-right">
                  <slot name="right" :item="option" />
                </div>
              </div>
            </SelectItem>
          </template>

          <div v-else class="own-select__empty">
            <OwnType :text="t('labels.empty')" variant="title-regular" />
          </div>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>

<script lang="ts" setup>
import { PhCaretDown } from '@phosphor-icons/vue'
import {
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from 'reka-ui'
import { computed, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnType } from '../OwnType'

import type { OwnSelectItem } from './types'

const props = withDefaults(
  defineProps<{
    accessibilityId?: string
    disabled?: boolean
    error?: boolean
    multiple?: boolean
    options: OwnSelectItem[]
    placeholder?: string
    visibleOptions?: number
  }>(),
  {
    accessibilityId: undefined,
    disabled: false,
    error: false,
    multiple: false,
    placeholder: undefined,
    visibleOptions: 4,
  }
)

const { t } = useI18n()

const modelValue = defineModel<string | number | (string | number)[]>()

const LINE_HEIGHT = 40
const PADDING_HEIGHT = 8

const selectedValue = computed(() => {
  if (props.multiple && Array.isArray(modelValue.value)) {
    // For multiple selection, return the first selected option for display
    const firstValue = modelValue.value[0]
    return props.options.find((option) => option.value === firstValue)
  }
  return props.options.find((option) => option.value === modelValue.value)
})

const maxContentHeight = ref(
  `${LINE_HEIGHT * props.visibleOptions + PADDING_HEIGHT}px`
)

watchEffect(() => {
  maxContentHeight.value = `${LINE_HEIGHT * props.visibleOptions + PADDING_HEIGHT}px`
})
</script>

<style lang="scss">
@use '@/styles/globals';

.own-select {
  width: 100%;
  position: relative;

  &__trigger {
    width: 100%;
    box-shadow: var(--shadow-card-default);
    background-color: var(--background-primary);
    cursor: pointer;
    outline: none;
    display: block;

    transition:
      globals.transitionOne(background-color), globals.transitionOne(border);

    &:hover,
    &:active,
    &:focus,
    &[data-state='open'] {
      background-color: var(--background-highlight);
      border-color: rgba(var(--raw-misc-brand), 0.05);
    }

    &--error {
      border-color: var(--status-danger);

      &:hover,
      &:active,
      &:focus {
        border-color: var(--status-danger);
      }
    }

    &--disabled,
    &--disabled:hover,
    &--disabled:active {
      cursor: not-allowed;
      color: var(--text-color-tertiary);
      background-color: var(--background-secondary);
      border-color: var(--divider);
    }
  }

  &__trigger-text-container {
    max-width: calc(100% - 24px);
  }

  &__content {
    background-color: var(--background-primary);
    border-radius: 8px;
    box-shadow: var(--shadow-card-default);
    padding: 4px;
    width: var(--reka-select-trigger-width);
    max-height: var(--max-content-height);
    overflow: hidden;
  }

  &__viewport {
    overflow-y: auto;
    max-height: 100%;
  }

  // Select item styling - matches OwnDropdownMenuItem exactly
  &__item {
    padding: 0;
    margin: 0;
    border-radius: 4px;
    background: transparent;
    border: none;
    outline: none;
    width: 100%;
    display: block;
    cursor: pointer;

    &[data-highlighted],
    &:hover {
      background-color: var(--background-highlight);
    }

    &[data-state='checked'] {
      background-color: var(--background-highlight);
    }

    &[data-disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__item-content {
    padding: 8px 10px;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    transition: background-color 100ms ease-out;
  }

  &__item-left {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__item-text {
    flex: 1;
    text-align: left;
    min-width: 0; // Allow truncation
  }

  &__item-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__item-indicator {
    color: var(--color-primary);
    display: flex;
    align-items: center;

    // Only show when item is selected
    opacity: 0;
    transition: opacity 100ms ease-in;
  }

  // Show indicator when item is selected
  &__item[data-state='checked'] &__item-indicator {
    opacity: 1;
  }

  &__empty {
    padding: 8px;
    text-align: center;
    color: var(--color-secondary);
  }
}
</style>
